/**
 * useUserRole Hook - React hook do zarządzania rolami użytkownika
 * wojskaspecjalne.pl
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getUserRoles,
  isAdmin,
  isModerator,
  userHasRole,
  userHasPermission,
  getUserPermissions,
} from '../lib/roleService';
import type { RoleName, UserRole, Permission } from '../types/database.types';

interface UseUserRoleReturn {
  roles: UserRole[];
  isAdmin: boolean;
  isModerator: boolean;
  isUser: boolean;
  permissions: Permission[];
  loading: boolean;
  error: Error | null;
  hasRole: (roleName: RoleName) => boolean;
  hasPermission: (permission: Permission) => boolean;
  refresh: () => Promise<void>;
}

/**
 * Hook do sprawdzania ról i uprawnień zalogowanego użytkownika
 */
export function useUserRole(): UseUserRoleReturn {
  const { user, loading: authLoading } = useAuth();
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isAdminFlag, setIsAdminFlag] = useState(false);
  const [isModeratorFlag, setIsModeratorFlag] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadedForUserId, setLoadedForUserId] = useState<string | null>(null);

  const fetchUserRoles = useCallback(async () => {
    console.log('[useUserRole] fetchUserRoles called, user:', user?.id, user?.email, 'authLoading:', authLoading);

    // Jeśli AuthContext jeszcze nie odtworzył sesji, nie podejmuj decyzji "brak ról".
    // To zapobiega sytuacji: refresh → user=null przez ułamek sekundy → RoleGuard odcina UI.
    if (authLoading) {
      // Jeśli już mamy załadowane role dla aktualnego usera, nie przechodź z powrotem w loading
      // (to powoduje odmontowanie guarded komponentów i utratę niezapisanych danych w formularzach).
      if (!hasLoadedOnce) {
        setLoading(true);
      }
      return;
    }
    
    if (!user) {
      console.log('[useUserRole] No user, clearing roles and permissions');
      setRoles([]);
      setPermissions([]);
      setIsAdminFlag(false);
      setIsModeratorFlag(false);
      setLoading(false);
      setHasLoadedOnce(true);
      setLoadedForUserId(null);
      return;
    }

    try {
      const isSameUser = loadedForUserId === user.id;
      // Jeżeli to refresh dla tego samego usera i już raz ładowaliśmy role,
      // nie wchodź ponownie w loading (background refresh).
      if (!hasLoadedOnce || !isSameUser) {
        setLoading(true);
      }
      setError(null);
      console.log('[useUserRole] Fetching roles and permissions for user:', user.id);

      const [userRoles, userPermissions, adminStatus, moderatorStatus] = await Promise.all([
        getUserRoles(user.id),
        getUserPermissions(user.id),
        isAdmin(user.id),
        isModerator(user.id),
      ]);

      console.log('[useUserRole] Fetched data:', {
        userRoles: userRoles.map(r => r.role_name),
        userPermissions,
        adminStatus,
        moderatorStatus
      });

      setRoles(userRoles);
      setPermissions(userPermissions);
      setIsAdminFlag(adminStatus);
      setIsModeratorFlag(moderatorStatus);
      setHasLoadedOnce(true);
      setLoadedForUserId(user.id);
    } catch (err) {
      console.error('[useUserRole] ❌ Error fetching user roles:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      setHasLoadedOnce(true);
    } finally {
      // Jeśli byliśmy w trybie "background refresh", loading już było false — zostawiamy.
      setLoading(prev => (prev ? false : prev));
      console.log('[useUserRole] Loading completed');
    }
  }, [user, authLoading, hasLoadedOnce, loadedForUserId]);

  useEffect(() => {
    fetchUserRoles();
  }, [fetchUserRoles]);

  const hasRole = useCallback(
    (roleName: RoleName): boolean => {
      return roles.some((role) => role.role_name === roleName);
    },
    [roles]
  );

  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      return permissions.includes(permission);
    },
    [permissions]
  );

  return {
    roles,
    isAdmin: isAdminFlag,
    isModerator: isModeratorFlag,
    isUser: roles.some((role) => role.role_name === 'user'),
    permissions,
    loading,
    error,
    hasRole,
    hasPermission,
    refresh: fetchUserRoles,
  };
}

/**
 * Hook do sprawdzania czy użytkownik ma konkretną rolę
 */
export function useHasRole(roleName: RoleName): { hasRole: boolean; loading: boolean } {
  const { user, loading: authLoading } = useAuth();
  const [hasRoleFlag, setHasRoleFlag] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadedForUserId, setLoadedForUserId] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) {
      if (!hasLoadedOnce) setLoading(true);
      return;
    }
    if (!user) {
      setHasRoleFlag(false);
      setLoading(false);
      setHasLoadedOnce(true);
      setLoadedForUserId(null);
      return;
    }

    let mounted = true;

    (async () => {
      try {
        const isSameUser = loadedForUserId === user.id;
        if (!hasLoadedOnce || !isSameUser) setLoading(true);
        const result = await userHasRole(user.id, roleName);
        if (mounted) {
          setHasRoleFlag(result);
          setHasLoadedOnce(true);
          setLoadedForUserId(user.id);
        }
      } catch (err) {
        console.error(`Error checking role ${roleName}:`, err);
        if (mounted) {
          setHasRoleFlag(false);
          setHasLoadedOnce(true);
        }
      } finally {
        if (mounted) {
          setLoading(prev => (prev ? false : prev));
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [user, roleName, authLoading, hasLoadedOnce, loadedForUserId]);

  return { hasRole: hasRoleFlag, loading };
}

/**
 * Hook do sprawdzania czy użytkownik ma konkretne uprawnienie
 */
export function useHasPermission(
  permission: Permission
): { hasPermission: boolean; loading: boolean } {
  const { user, loading: authLoading } = useAuth();
  const [hasPermissionFlag, setHasPermissionFlag] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadedForUserId, setLoadedForUserId] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) {
      if (!hasLoadedOnce) setLoading(true);
      return;
    }
    if (!user) {
      setHasPermissionFlag(false);
      setLoading(false);
      setHasLoadedOnce(true);
      setLoadedForUserId(null);
      return;
    }

    let mounted = true;

    (async () => {
      try {
        const isSameUser = loadedForUserId === user.id;
        if (!hasLoadedOnce || !isSameUser) setLoading(true);
        const result = await userHasPermission(user.id, permission);
        if (mounted) {
          setHasPermissionFlag(result);
          setHasLoadedOnce(true);
          setLoadedForUserId(user.id);
        }
      } catch (err) {
        console.error(`Error checking permission ${permission}:`, err);
        if (mounted) {
          setHasPermissionFlag(false);
          setHasLoadedOnce(true);
        }
      } finally {
        if (mounted) {
          setLoading(prev => (prev ? false : prev));
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [user, permission, authLoading, hasLoadedOnce, loadedForUserId]);

  return { hasPermission: hasPermissionFlag, loading };
}

